<template>
  <b-sidebar
    :visible="isSideBarActive"
    bg-variant="white"
    sidebar-class="sidebar-lg"
    shadow
    backdrop
    no-header
    right
    @change="(val) => $emit('update:isSideBarActive', val)"
  >
    <template #default="{ hide }">
      <div class="d-flex justify-content-between align-items-center content-sidebar-header px-2 py-1">
        <h5 class="mb-0">
          Add Money
        </h5>
        <feather-icon
          class="ml-1 cursor-pointer"
          icon="XIcon"
          size="16"
          @click="hide"
        />
      </div>
      <validation-observer ref="userAddMoneyObserver">
        <b-form
          autocomplete="off"
          class="p-2"
          @submit.prevent="submitUserTransaction"
        >
          <validation-provider
            #default="validationContext"
            name="Transaction Type"
            rules="required"
          >
            <b-form-group
              v-if="transactionTypes"
              label="Transaction Type"
              label-for="transaction-types"
            >
              <v-select
                id="transaction-types"
                v-model="transaction.transactionTypeId"
                :options="transactionTypes"
                :clearable="false"
                :reduce="(option) => option.id"
              />
              <small class="text-danger">{{ validationContext.errors[0] }}</small>
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-if="transactionType === 'add'"
            #default="validationContext"
            name="Credit Amount"
            rules="required"
          >

            <b-form-group
              label="Credit Amount"
              label-for="credit-amount"
            >
              <b-form-input
                id="credit-amount"
                v-model="transaction.creditAmount"
                type="number"
                step="any"
                @input="positiveValidation"
              />
              <small class="text-danger">{{ validationContext.errors[0] }}</small>

              <small
                v-if="!isPositiveField"
                class="text-danger"
              >Kindly enter a value bigger than 0</small>
            </b-form-group>
          </validation-provider>
          <validation-provider
            v-else
            #default="validationContext"
            name="Debit Amount"
            rules="required"
          >
            <b-form-group
              label="Debit Amount"
              label-for="debit-amount"
            >
              <b-form-input
                id="debit-amount"
                v-model.number="transaction.debitAmount"
                type="number"
                step="any"
                @input="positiveValidation"
              />
              <small class="text-danger">{{ validationContext.errors[0] }}</small>
              <small
                v-if="!isPositiveField"
                class="text-danger"
              >Kindly enter a value bigger than 0</small>
            </b-form-group>
          </validation-provider>
          <validation-provider
            #default="validationContext"
            name="Reason"
            rules="required"
          >

            <b-form-group
              label="Reason"
              label-for="add-money-reason"
            >
              <b-form-input
                id="add-money-reason"
                v-model="transaction.remarkText"
              />
              <small class="text-danger">{{ validationContext.errors[0] }}</small>
            </b-form-group>
          </validation-provider>
          <b-button
            variant="primary"
            type="submit"
          >
            <b-spinner
              v-if="submitBtnLoading"
              small
            />
            <span v-else>Submit</span>
          </b-button>
        </b-form>
      </validation-observer>
    </template>
  </b-sidebar>
</template>

<script>
import {
  BSidebar, BForm, BFormGroup, BFormInput, BButton, BSpinner,
} from 'bootstrap-vue'
import {
  computed, ref, reactive, watch,
} from '@vue/composition-api'
import ToastificationContent from '@core/components/toastification/ToastificationContent.vue'
import { useToast } from 'vue-toastification/composition'
import vSelect from 'vue-select'
import { required } from '@validations'
import { ValidationProvider, ValidationObserver } from 'vee-validate'
import { errorNotification } from '@/utils/toast-notifications'

export default {
  components: {
    BSidebar,
    BForm,
    BFormGroup,
    BFormInput,
    BButton,
    BSpinner,
    ValidationProvider,
    ValidationObserver,
    vSelect,
  },
  model: {
    prop: 'isSideBarActive',
    event: 'update:isSideBarActive',
  },
  props: {
    isSideBarActive: {
      type: Boolean,
      required: true,
    },
    transactionType: {
      type: String,
      default: 'add',
    },
    isPendingTransaction: {
      type: Boolean,
      default: false,
    },
  },
  setup(props, ctx) {
    const toast = useToast()
    const userAddMoneyObserver = ref(null)
    const wallet = computed(() => ctx.root.$store.getters['users/getWalletDetails'])
    const user = computed(() => ctx.root.$store.getters['auth/getLoggedUser'])
    const transactionState = () => ({
      transactionTypeId: null,
      walletId: wallet.value?.id,
      creditAmount: 0,
      debitAmount: 0,
      remarkText: '',
      addedBy: user.value?.email,
    })
    const transaction = reactive(transactionState())

    const transactionTypes = computed(() => {
      let types = ctx.root.$store.getters['transactionTypes/getTransactionTypes']
      if (types) {
        if (props.transactionType === 'add') {
          types = types.filter(el => el.visible_credit_list === true)
        } else {
          types = types.filter(type => type.visible_debit_list === true)
        }
      }
      return types
    })
    const isPositiveField = ref(true)
    const positiveValidation = val => {
      if (val.startsWith('-')) {
        isPositiveField.value = false
      } else {
        isPositiveField.value = true
      }
    }

    const submitBtnLoading = ref(false)

    const submitUserTransaction = async () => {
      if (ctx.root.$can('write', 'max-wallet-transactions')) {
        if (props.transactionType === 'add' && transaction.creditAmount > 3) {
          errorNotification('Maximum value allowed is 3')
          return
        }
        if (props.transactionType !== 'add' && transaction.debitAmount > 3) {
          errorNotification('Maximum value allowed is 3')
          return
        }
      }
      try {
        if (!isPositiveField.value) {
          return
        }
        const isValid = await userAddMoneyObserver.value.validate()
        if (!isValid) return
        submitBtnLoading.value = true
        if (props.isPendingTransaction) {
          await ctx.root.$store.dispatch('users/addUserPendingTransaction', transaction)
        } else {
          await ctx.root.$store.dispatch('users/addUserTransaction', {
            transaction,
            id: ctx.root.$route.query.externalId,
          })
        }
        toast({
          component: ToastificationContent,
          props: {
            title: 'Transaction Saved Successfully',
            icon: 'CoffeeIcon',
            variant: 'success',
          },
        })
        ctx.emit('update:isSideBarActive', false)
        ctx.emit('refetch-user-wallet')
      } catch (error) {
        console.log(error)
      } finally {
        submitBtnLoading.value = false
      }
    }
    watch(() => props.isSideBarActive, () => {
      Object.assign(transaction, transactionState())
      userAddMoneyObserver.value.reset()
      isPositiveField.value = true
    })

    return {
      userAddMoneyObserver,
      transaction,
      transactionTypes,
      isPositiveField,
      positiveValidation,
      submitUserTransaction,
      submitBtnLoading,
      required,
    }
  },
}
</script>

<style>

</style>
